import { Controller } from "@hotwired/stimulus";
import Carousel from "bootstrap/js/dist/carousel";

/**
 * Hero Carousel Controller
 *
 * Manages the hero carousel with video backgrounds, auto-advance, and interactive controls.
 * Features:
 * - Video playback control (play active slide, pause others)
 * - Auto-advance with 6-second intervals
 * - Pause on hover
 * - Mute/unmute toggle (pauses auto-advance when unmuted)
 * - Overlay fade when video unmuted
 * - Reset timer on manual navigation
 * - Sync dot progress animations
 * - Page Visibility API integration (pause when tab hidden)
 */
export default class extends Controller {
  static targets = [
    "carousel",
    "slide",
    "video",
    "overlay",
    "indicator",
    "videoControls",
    "playButton",
    "playIcon",
    "pauseIcon",
    "muteButton",
    "mutedIcon",
    "unmutedIcon"
  ];

  connect() {
    // Initialize state
    this.autoAdvanceInterval = 6000; // 6 seconds
    this.autoAdvanceTimer = null;
    this.isPlaying = true;
    this.isMuted = true;
    this.isPaused = false;
    this.isHovering = false;

    // Get Bootstrap carousel instance
    this.bootstrapCarousel = Carousel.getInstance(this.carouselTarget) ||
                              new Carousel(this.carouselTarget, {
                                interval: false, // We'll manage this ourselves
                                ride: false
                              });

    // Set up event listeners
    this.setupEventListeners();

    // Start auto-advance
    this.startAutoAdvance();

    // Play the first video and update controls visibility
    this.playCurrentVideo();
    this.updateVideoControls();

    // Add loaded class for fade-in animation
    setTimeout(() => {
      this.element.classList.add('loaded');
    }, 100);
  }

  disconnect() {
    this.stopAutoAdvance();
    this.removeEventListeners();
  }

  setupEventListeners() {
    // Bootstrap carousel events
    this.carouselTarget.addEventListener('slide.bs.carousel', this.handleSlideStart.bind(this));
    this.carouselTarget.addEventListener('slid.bs.carousel', this.handleSlideEnd.bind(this));

    // Hover events (pause auto-advance on hover)
    this.element.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
    this.element.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

    // Page Visibility API (pause when tab hidden)
    this.visibilityChangeHandler = this.handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  removeEventListeners() {
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  // ==========================================
  // AUTO-ADVANCE MANAGEMENT
  // ==========================================

  startAutoAdvance() {
    // Don't start if paused, hovering, unmuted, or tab hidden
    if (this.isPaused || this.isHovering || !this.isMuted || document.hidden) {
      return;
    }

    this.stopAutoAdvance();

    this.autoAdvanceTimer = setInterval(() => {
      this.bootstrapCarousel.next();
    }, this.autoAdvanceInterval);

    // Restart dot animation
    this.restartDotAnimation();
  }

  stopAutoAdvance() {
    if (this.autoAdvanceTimer) {
      clearInterval(this.autoAdvanceTimer);
      this.autoAdvanceTimer = null;
    }

    // Pause all dot animations
    this.pauseDotAnimations();
  }

  resetAutoAdvance() {
    this.stopAutoAdvance();
    this.startAutoAdvance();
  }

  // ==========================================
  // CAROUSEL EVENTS
  // ==========================================

  handleSlideStart(event) {
    // Pause all videos when slide starts transitioning
    this.pauseAllVideos();
    this.stopAutoAdvance();
  }

  handleSlideEnd(event) {
    // Update indicator active states
    this.updateIndicators(event.to);

    // Play the new active video and show/hide video controls
    this.playCurrentVideo();
    this.updateVideoControls();

    // Restart auto-advance
    this.resetAutoAdvance();
  }

  // ==========================================
  // VIDEO PLAYBACK CONTROL
  // ==========================================

  playCurrentVideo() {
    const activeSlide = this.slideTargets.find(slide =>
      slide.classList.contains('active')
    );

    if (activeSlide) {
      const video = activeSlide.querySelector('[data-hero-carousel-target="video"]');
      if (video && video.querySelector('source')) {
        // Only try to play if video has a source
        video.play().catch(err => {
          console.warn('Video autoplay blocked:', err);
        });
      }
    }
  }

  currentSlideHasVideo() {
    const activeSlide = this.slideTargets.find(slide =>
      slide.classList.contains('active')
    );
    if (!activeSlide) return false;
    const video = activeSlide.querySelector('[data-hero-carousel-target="video"]');
    return video && video.querySelector('source') !== null;
  }

  updateVideoControls() {
    if (!this.hasVideoControlsTarget) return;
    const hasVideo = this.currentSlideHasVideo();
    this.videoControlsTarget.style.display = hasVideo ? 'contents' : 'none';
  }

  pauseAllVideos() {
    this.videoTargets.forEach(video => {
      if (!video.paused) {
        video.pause();
      }
    });
  }

  // ==========================================
  // PLAY/PAUSE CONTROL
  // ==========================================

  togglePlay() {
    if (this.isPaused) {
      this.play();
    } else {
      this.pause();
    }
  }

  play() {
    this.isPaused = false;
    this.playCurrentVideo();
    this.startAutoAdvance();

    // Update button icons
    this.playIconTarget.classList.add('d-none');
    this.pauseIconTarget.classList.remove('d-none');
  }

  pause() {
    this.isPaused = true;
    this.pauseAllVideos();
    this.stopAutoAdvance();

    // Update button icons
    this.playIconTarget.classList.remove('d-none');
    this.pauseIconTarget.classList.add('d-none');
  }

  // ==========================================
  // MUTE/UNMUTE CONTROL
  // ==========================================

  toggleMute() {
    if (this.isMuted) {
      this.unmute();
    } else {
      this.mute();
    }
  }

  unmute() {
    this.isMuted = false;

    // Unmute all videos
    this.videoTargets.forEach(video => {
      video.muted = false;
    });

    // Pause auto-advance when unmuted
    this.stopAutoAdvance();

    // Fade overlays to 50% opacity
    this.overlayTargets.forEach(overlay => {
      overlay.style.transition = 'opacity 0.5s ease';
      overlay.style.opacity = '0.5';
    });

    // Update button icons
    this.mutedIconTarget.classList.add('d-none');
    this.unmutedIconTarget.classList.remove('d-none');
  }

  mute() {
    this.isMuted = true;

    // Mute all videos
    this.videoTargets.forEach(video => {
      video.muted = true;
    });

    // Resume auto-advance when muted
    this.startAutoAdvance();

    // Restore overlays to full opacity
    this.overlayTargets.forEach(overlay => {
      overlay.style.transition = 'opacity 0.5s ease';
      overlay.style.opacity = '1';
    });

    // Update button icons
    this.mutedIconTarget.classList.remove('d-none');
    this.unmutedIconTarget.classList.add('d-none');
  }

  // ==========================================
  // DOT ANIMATION CONTROL
  // ==========================================

  updateIndicators(activeIndex) {
    this.indicatorTargets.forEach((indicator, index) => {
      const progress = indicator.querySelector('.mpi-hero-indicator-progress');
      if (index === activeIndex) {
        indicator.classList.add('active');
        if (progress) {
          // Force reflow to restart animation on newly active indicator
          progress.style.animation = 'none';
          progress.style.animationPlayState = '';
          progress.offsetHeight; // Trigger reflow
          progress.style.animation = '';
        }
      } else {
        indicator.classList.remove('active');
        if (progress) {
          // Clear animation on inactive indicators
          progress.style.animation = 'none';
        }
      }
    });
  }

  restartDotAnimation() {
    const activeIndex = this.indicatorTargets.findIndex(i =>
      i.classList.contains('active')
    );
    this.updateIndicators(activeIndex >= 0 ? activeIndex : 0);
  }

  pauseDotAnimations() {
    this.indicatorTargets.forEach(indicator => {
      const progress = indicator.querySelector('.mpi-hero-indicator-progress');
      if (progress) {
        progress.style.animationPlayState = 'paused';
      }
    });
  }

  // ==========================================
  // HOVER EVENTS
  // ==========================================

  handleMouseEnter() {
    this.isHovering = true;
    this.stopAutoAdvance();
  }

  handleMouseLeave() {
    this.isHovering = false;
    this.startAutoAdvance();
  }

  // ==========================================
  // PAGE VISIBILITY
  // ==========================================

  handleVisibilityChange() {
    if (document.hidden) {
      // Pause everything when tab is hidden
      this.stopAutoAdvance();
      this.pauseAllVideos();
    } else {
      // Resume when tab is visible
      if (!this.isPaused) {
        this.playCurrentVideo();
        this.startAutoAdvance();
      }
    }
  }
}
